/**
 * Image Processing Utility
 * Handles image resize, compression, and validation
 */

export interface ImageValidationResult {
  isValid: boolean
  error?: string
  width?: number
  height?: number
  size?: number
}

export interface ProcessedImage {
  file: Blob
  preview: string
  width: number
  height: number
  size: number
  originalSize: number
}

const MIN_WIDTH = 600
const MIN_HEIGHT = 800
const MAX_FILE_SIZE = 2 * 1024 * 1024 // 2MB

/**
 * Validate image dimensions and file size
 */
export const validateImage = (file: File): ImageValidationResult => {
  // Check file type
  if (!['image/jpeg', 'image/png'].includes(file.type)) {
    return {
      isValid: false,
      error: 'Format hanya JPG atau PNG yang diizinkan',
    }
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(2)
    return {
      isValid: false,
      error: `Ukuran file terlalu besar (${sizeMB}MB). Maksimal 2MB.`,
      size: file.size,
    }
  }

  return { isValid: true, size: file.size }
}

/**
 * Get image dimensions without processing
 * Note: Does NOT validate minimum size - that's handled by processImage
 */
export const getImageDimensions = (file: File): Promise<ImageValidationResult> => {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = (e: any) => {
      const img = new Image()
      img.onload = () => {
        const width = img.naturalWidth
        const height = img.naturalHeight

        // Just return the dimensions, don't validate size here
        // Let processImage handle resize if needed
        resolve({ isValid: true, width, height })
      }
      img.onerror = () => {
        resolve({ isValid: false, error: 'Gagal membaca dimensi foto' })
      }
      img.src = e.target.result
    }
    reader.onerror = () => {
      resolve({ isValid: false, error: 'Gagal membaca file' })
    }
    reader.readAsDataURL(file)
  })
}

/**
 * Resize and compress image
 */
export const processImage = (file: File, quality: number = 0.85): Promise<ProcessedImage> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e: any) => {
      const img = new Image()

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas')
          let width = img.naturalWidth
          let height = img.naturalHeight
          const originalSize = file.size

          // Resize if smaller than minimum
          if (width < MIN_WIDTH || height < MIN_HEIGHT) {
            const aspectRatio = width / height
            const minRatio = MIN_WIDTH / MIN_HEIGHT

            if (aspectRatio > minRatio) {
              // Width is relatively larger, fix height
              height = MIN_HEIGHT
              width = Math.round(height * aspectRatio)
            } else {
              // Height is relatively larger, fix width
              width = MIN_WIDTH
              height = Math.round(width / aspectRatio)
            }
          }

          canvas.width = width
          canvas.height = height

          const ctx = canvas.getContext('2d')
          if (!ctx) {
            throw new Error('Canvas 2D context not available')
          }

          // Draw image on canvas
          ctx.drawImage(img, 0, 0, width, height)

          // Convert to blob with compression
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to convert canvas to blob'))
                return
              }

              const preview = canvas.toDataURL('image/jpeg', quality)

              resolve({
                file: blob,
                preview,
                width,
                height,
                size: blob.size,
                originalSize,
              })
            },
            'image/jpeg',
            quality
          )
        } catch (error) {
          reject(error)
        }
      }

      img.onerror = () => reject(new Error('Failed to load image from data URL'))
      img.src = e.target.result
    }

    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

/**
 * Calculate compression ratio
 */
export const getCompressionRatio = (originalSize: number, compressedSize: number): number => {
  if (originalSize === 0) return 0
  return Math.round(((originalSize - compressedSize) / originalSize) * 100)
}
